import { useRef, useState, useCallback } from 'react'
import { CAL_START_HOUR, CAL_END_HOUR, HOUR_HEIGHT } from './calendarConstants'

export type DragType = 'draft' | 'break' | 'tm'

export interface DragState {
  type: DragType
  /** Current hover day — changes on every cross-day move */
  dayIso: string
  /** Day where the drag started — stays constant throughout the drag */
  originDayIso: string
  entryId?: number
  /** Raw (unsnapped) top position in pixels — use this for visual placement during drag */
  topPx: number
  /** Snapped hours/minutes — used only on drop to determine the final time */
  hours: number
  minutes: number
}

export interface TapInfo {
  type: DragType
  dayIso: string
  entryId?: number
  anchorRect: DOMRect
}

interface DragRef {
  type: DragType
  dayIso: string
  originDayIso: string
  entryId?: number
  anchorRect: DOMRect
  gridEl: HTMLElement
  offsetY: number
  startX: number
  startY: number
  moved: boolean
}

const MIN_MINUTES = CAL_START_HOUR * 60
const MAX_MINUTES = CAL_END_HOUR * 60 - 15
const DRAG_THRESHOLD_PX = 10

function snapToGrid(pointerYInGrid: number, offsetY: number) {
  const rawTopPx = pointerYInGrid - offsetY
  const minutesFromGridStart = (rawTopPx / HOUR_HEIGHT) * 60
  const totalMinutes = CAL_START_HOUR * 60 + minutesFromGridStart
  const rounded = Math.round(totalMinutes / 15) * 15
  const clamped = Math.max(MIN_MINUTES, Math.min(rounded, MAX_MINUTES))
  const minTopPx = 0
  const maxTopPx = ((MAX_MINUTES - MIN_MINUTES) / 60) * HOUR_HEIGHT
  const visualTopPx = Math.max(minTopPx, Math.min(rawTopPx, maxTopPx))
  return {
    topPx: visualTopPx,
    hours: Math.floor(clamped / 60),
    minutes: clamped % 60,
  }
}

export function useCalendarBlockDrag(
  onDrop: (type: DragType, dayIso: string, hours: number, minutes: number, entryId?: number, anchorRect?: DOMRect) => void,
  onTap?: (info: TapInfo) => void,
) {
  const dragRef = useRef<DragRef | null>(null)
  const lastSnapRef = useRef<{ hours: number; minutes: number } | null>(null)
  const [drag, setDrag] = useState<DragState | null>(null)

  const startDrag = useCallback(
    (
      type: DragType,
      dayIso: string,
      e: React.PointerEvent,
      gridEl: HTMLElement,
      blockTopPx: number,
      entryId?: number,
      anchorRect?: DOMRect,
    ) => {
      e.preventDefault()
      e.stopPropagation()

      const gridRect = gridEl.getBoundingClientRect()
      const pointerYInGrid = e.clientY - gridRect.top
      const offsetY = pointerYInGrid - blockTopPx

      dragRef.current = {
        type, dayIso, originDayIso: dayIso, entryId,
        anchorRect: anchorRect ?? (e.currentTarget as HTMLElement).getBoundingClientRect(),
        gridEl, offsetY,
        startX: e.clientX, startY: e.clientY, moved: false,
      }

      const snapped = snapToGrid(pointerYInGrid, offsetY)
      lastSnapRef.current = { hours: snapped.hours, minutes: snapped.minutes }

      document.body.style.userSelect = 'none'

      function handleMove(ev: PointerEvent) {
        if (!dragRef.current) return
        ev.preventDefault()

        if (!dragRef.current.moved) {
          const dx = Math.abs(ev.clientX - dragRef.current.startX)
          const dy = Math.abs(ev.clientY - dragRef.current.startY)
          if (dx < DRAG_THRESHOLD_PX && dy < DRAG_THRESHOLD_PX) return
          dragRef.current.moved = true
          setDrag({ type, dayIso: dragRef.current.dayIso, originDayIso: dragRef.current.originDayIso, entryId, ...snapped })
        }

        const elUnder = document.elementFromPoint(ev.clientX, ev.clientY)
        const hoveredGrid = elUnder?.closest<HTMLElement>('[data-planning-grid]')
        if (hoveredGrid) {
          dragRef.current.dayIso = hoveredGrid.dataset.dayIso!
          dragRef.current.gridEl = hoveredGrid
        }

        const rect = dragRef.current.gridEl.getBoundingClientRect()
        const yInGrid = ev.clientY - rect.top
        const snap = snapToGrid(yInGrid, dragRef.current.offsetY)
        lastSnapRef.current = { hours: snap.hours, minutes: snap.minutes }
        setDrag({
          type: dragRef.current.type,
          dayIso: dragRef.current.dayIso,
          originDayIso: dragRef.current.originDayIso,
          entryId: dragRef.current.entryId,
          ...snap,
        })
      }

      function handleUp() {
        const snap = lastSnapRef.current
        const ref = dragRef.current
        cleanup()
        if (!ref) return
        if (ref.moved && snap) {
          onDrop(ref.type, ref.dayIso, snap.hours, snap.minutes, ref.entryId, ref.anchorRect)
        } else {
          onTap?.({ type: ref.type, dayIso: ref.dayIso, entryId: ref.entryId, anchorRect: ref.anchorRect })
        }
      }

      function cleanup() {
        dragRef.current = null
        lastSnapRef.current = null
        setDrag(null)
        document.removeEventListener('pointermove', handleMove)
        document.removeEventListener('pointerup', handleUp)
        document.removeEventListener('pointercancel', cleanup)
        document.body.style.userSelect = ''
      }

      document.addEventListener('pointermove', handleMove)
      document.addEventListener('pointerup', handleUp)
      document.addEventListener('pointercancel', cleanup)
    },
    [onDrop, onTap],
  )

  return { drag, startDrag }
}
